import React, { createContext, useState, useContext, useEffect } from 'react';

import { Product } from '../../pages/Merch/data/products';

interface CartItem extends Product {
    quantity: number;
    selectedSize: string;
}

interface CartContextValue {
    cartItems: CartItem[];
    addToCart: (product: Product, size: string) => void;
    removeFromCart: (id: string) => void;
    clearCart: () => void;
    updateItemQuantity: (id: string, quantity: number) => void; 
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export const CartProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
    const [cartItems, setCartItems] = useState<CartItem[]>(() => {
        const storedCart = localStorage.getItem('cart');
        return storedCart ? JSON.parse(storedCart) : [];
    });

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cartItems));
    }, [cartItems]);

    const addToCart = (product: Product, size: string) => {
        setCartItems((prevItems) => {
            const existingItem = prevItems.find(
                (item) => item.id === product.id && item.selectedSize === size
            );

            if (existingItem) {
                return prevItems.map((item) =>
                    item.id === product.id && item.selectedSize === size
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            } else {
                return [
                    ...prevItems,
                    { 
                        ...product, 
                        quantity: 1, 
                        selectedSize: size 
                    },
                ];
            }
        });
    };

    const removeFromCart = (id: string) => {
        setCartItems(prevItems => prevItems.filter(item => item.id !== id));
    };

    const updateItemQuantity = (id: string, quantity: number) => {
        setCartItems(prevItems => 
            prevItems.map(item => {
                if (item.id === id) {
                    return { ...item, quantity };
                }
                return item;
            })
        );
    };

    const clearCart = () => {
        setCartItems([]);
    };

    return (
        <CartContext.Provider value={{ 
            cartItems, 
            addToCart, 
            removeFromCart, 
            clearCart,
            updateItemQuantity 
            }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

